import { create } from "zustand";

/**
 * In-app confirmation dialogs for the web build.
 *
 * `showConfirm` (utils/alert.ts) used to be `window.confirm` on web. Safari
 * offers a "Don't allow this page to create more dialogs" checkbox on the
 * second native dialog in a row — the delete-account flow fires exactly two —
 * and once ticked every later `window.confirm` returns false immediately, so
 * Sign Out, Delete and Export silently did nothing with no error path. A
 * dialog the app renders itself cannot be suppressed, and it wears the theme.
 *
 * Requests queue so two confirms in quick succession are shown one after the
 * other rather than the second clobbering the first.
 */

export interface ConfirmRequest {
  id: number;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  destructive: boolean;
}

interface ConfirmDialogState {
  current: ConfirmRequest | null;
  /** Ask the user. Resolves true on confirm, false on cancel or dismiss. */
  ask: (request: Omit<ConfirmRequest, "id">) => Promise<boolean>;
  /** Settle the current request; the host calls this from its buttons. */
  settle: (confirmed: boolean) => void;
}

interface Pending extends ConfirmRequest {
  resolve: (confirmed: boolean) => void;
}

let nextId = 1;
const queue: Pending[] = [];

export const useConfirmDialogStore = create<ConfirmDialogState>((set, get) => {
  const promote = () => {
    const next = queue[0] ?? null;
    set({ current: next ? stripResolver(next) : null });
  };

  return {
    current: null,

    ask: (request) =>
      new Promise<boolean>((resolve) => {
        queue.push({ ...request, id: nextId++, resolve });
        if (!get().current) promote();
      }),

    settle: (confirmed) => {
      const done = queue.shift();
      done?.resolve(confirmed);
      promote();
    },
  };
});

function stripResolver({ resolve: _resolve, ...request }: Pending): ConfirmRequest {
  return request;
}
